import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';

import { openAlert as openAlertAction } from '../../../../../../actions/modal-actions';
import useRosterIdentitiesCreate from '../../hooks/useRosterIdentitiesCreate';
import InvalidRowEditor from './components/InvalidRowEditor';
import ImportInstructionsModal from './components/ImportInstructionsModal';
import ImportIcon from '../../../../../../components/icons/ImportIcon';
import UploadPlaceholder from './components/UploadPlaceholder';
import ColumnMatcher from './components/ColumnMatcher';
import InfoCircleIcon from '../../../../../../components/icons/InfoCircleIcon';

const propTypes = {
  onSuccess: PropTypes.func,
  openAlert: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func.isRequired,
};

const AddressBookCSVUpload = ({ onSuccess, openAlert, onRequestClose }) => {
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [uploadData, setUploadData] = useState();
  const {
    onRosterIdentitiesCreate,
    onRosterIdentitiesCreateFromColumnMatchedData,
    success,
    loading,
    errors,
    createdRosterIdentities,
    invalidRosterIdentities,
    validateRosterIdentities,
  } = useRosterIdentitiesCreate();

  const [hasValidationErrors, setHasValidationErrors] = useState(false);

  useEffect(() => {
    if (success) {
      openAlert({
        type: 'success',
        message: 'Your contacts have been imported.',
      });

      onSuccess?.();
      onRequestClose();
    }
  }, [success]);

  useEffect(() => {
    if (errors.length > 0) {
      openAlert({
        type: 'error',
        message:
          'CSV parsing errors were found, please address them and re-upload:',
        info: (
          <ul className="alert-modal-content-info text-left">
            {errors.map((rosterIdentitiesCreateError, index) => (
              <li key={index} className="mb-1">
                {rosterIdentitiesCreateError}
              </li>
            ))}
          </ul>
        ),
      });

      // These errors come from GQL. Nothing we can do from our end to fix them.
      // User needs to check their CSV and adjust accordingly.
      onRequestClose();
    }
  }, [errors.length]);

  useEffect(() => {
    if (invalidRosterIdentities.length > 0) {
      setHasValidationErrors(true);
    }
  }, [invalidRosterIdentities.length]);

  return (
    <Fragment>
      <div className="d-flex align-items-center mb-3 mr-4">
        <h3 className="d-flex align-items-center m-0">
          <ImportIcon color="#1a2e34" className="mr-1" size={16} /> Import
          contacts
        </h3>

        <a
          className="ml-auto text-extra-bold text-sm"
          href="#"
          onClick={event => {
            event.preventDefault();
            setInstructionsOpen(true);
          }}
        >
          <InfoCircleIcon size={12} /> Import Instructions
        </a>
      </div>

      {!uploadData && (
        <UploadPlaceholder
          onDataReceived={data => {
            setUploadData(data);
          }}
          onCancel={onRequestClose}
        />
      )}

      {uploadData && !hasValidationErrors && (
        <ColumnMatcher
          data={uploadData}
          onContinue={data => {
            onRosterIdentitiesCreateFromColumnMatchedData(data);
          }}
          onCancel={onRequestClose}
          loading={loading}
        />
      )}

      {uploadData && hasValidationErrors && (
        <InvalidRowEditor
          createdRosterIdentities={createdRosterIdentities}
          invalidRosterIdentities={invalidRosterIdentities}
          validateRosterIdentities={validateRosterIdentities}
          onSubmit={rosterIdentities => {
            onRosterIdentitiesCreate(rosterIdentities);
          }}
          onCancel={() => {
            // Reset uploader
            setHasValidationErrors(false);
            onRequestClose();
          }}
        />
      )}

      <ImportInstructionsModal
        forceOpen={instructionsOpen}
        onClose={() => setInstructionsOpen(false)}
      />
    </Fragment>
  );
};

AddressBookCSVUpload.propTypes = propTypes;

const mapDispatchToProps = {
  openAlert: openAlertAction,
};

export { AddressBookCSVUpload as AddressBookCSVUploadComponent };

export default compose(connect(null, mapDispatchToProps))(AddressBookCSVUpload);
